import { z } from 'zod/v4'

export const paginationOptionsSchema = z.object({
	perPage: z.number().optional().default(10),
	page: z.number().optional().default(1),
})

export type PaginationOptions = z.infer<typeof paginationOptionsSchema>

export const createQueryOptionsSchema = (sortableFields: readonly string[], defaultSortBy: string) => {
	return z
		.object({
			sortBy: z
				.enum(sortableFields as [string, ...string[]])
				.optional()
				.default(defaultSortBy),
			order: z.enum(['asc', 'desc']).optional().default('asc'),
		})
		.merge(paginationOptionsSchema)
}

export type QueryOptions = z.infer<ReturnType<typeof createQueryOptionsSchema>>

export enum StepType {
	CYCLE_START = 'cycle_start',
	EDIT_REVIEWERS_LIST = 'edit_rater_list',
	APPROVE_REVIEWERS_LIST = 'approve_rater_list',
	SELF_EVALUATION = 'self_evaluation',
	GROUP_EVALUATION = 'group_evaluation',
	MANAGER_EVALUATION = 'manager_evaluation',
	UNSOLICITED_EVALUATION = 'unsolicited_evaluation',
	DIRECT_REPORTS_EVALUATION = 'direct_reports_evaluation',
	PEER_EVALUATION = 'peer_evaluation',
	DOWN_EVALUATION = 'down_evaluation',
	UP_EVALUATION = 'up_evaluation',
	CALIBRATION = 'calibration',
	REVIEW = 'review',
	RELEASE = 'release',
	ACKNOWLEDGE = 'acknowledge',
	EMAIL = 'email',
	HOLD = 'hold',
	CYCLE_END = 'cycle_end',
}

export enum CycleReviewersStatus {
	PENDING = 'pending',
	SUBMITTED = 'submitted',
	APPROVED = 'approved',
	REJECTED = 'rejected',
}

export enum CalculatedRole {
	RATER = 'rater',
	UNSOLICITED = 'unsolicited',
	PARTICIPANT = 'participant',
	PRIMARY_MANAGER = 'primary_manager',
	CO_MANAGER = 'co_manager',
	SECONDARY_MANAGER = 'secondary_manager',
	HIGH_LEVEL_MANAGER = 'high_level_manager',
	DIRECT_REPORTS = 'direct_reports',
	PEER = 'peer',
	DOWN = 'down',
	UP = 'up',
}

export const CalculatedRoleDisplayName: Record<CalculatedRole, string> = {
	[CalculatedRole.RATER]: 'Rater',
	[CalculatedRole.UNSOLICITED]: 'Unsolicited',
	[CalculatedRole.PARTICIPANT]: 'Self',
	[CalculatedRole.PRIMARY_MANAGER]: 'Direct Manager',
	[CalculatedRole.CO_MANAGER]: 'Co-Manager',
	[CalculatedRole.SECONDARY_MANAGER]: 'Secondary Manager',
	[CalculatedRole.HIGH_LEVEL_MANAGER]: 'High Level Manager',
	[CalculatedRole.DIRECT_REPORTS]: 'Direct Reports',
	[CalculatedRole.PEER]: 'Peer',
	[CalculatedRole.DOWN]: 'Downward',
	[CalculatedRole.UP]: 'Upward',
}

export type CoManagerResponseMode = 'independent' | 'shared'

/**
 * Manager roles that should be grouped together on the dashboard.
 * Co-manager tasks are displayed alongside primary manager tasks.
 * Note: This is specifically for dashboard grouping, not for filtering all manager submissions.
 */
export const MANAGER_ROLES: CalculatedRole[] = [CalculatedRole.PRIMARY_MANAGER, CalculatedRole.CO_MANAGER]

/**
 * All roles that represent manager evaluations.
 * Use this constant when filtering submissions to show all manager feedback
 * (e.g., in acknowledge step, reports, PDFs).
 *
 * This includes:
 * - PRIMARY_MANAGER: Direct manager
 * - SECONDARY_MANAGER: Secondary reporting manager
 * - HIGH_LEVEL_MANAGER: Skip-level manager
 * - CO_MANAGER: Co-manager sharing responsibilities
 */
export const ALL_MANAGER_ROLES: CalculatedRole[] = [
	CalculatedRole.PRIMARY_MANAGER,
	CalculatedRole.SECONDARY_MANAGER,
	CalculatedRole.HIGH_LEVEL_MANAGER,
	CalculatedRole.CO_MANAGER,
]

/**
 * Check if a calculated role represents a manager role.
 * Use this to filter submissions by role instead of by evaluatorId,
 * which ensures manager feedback remains visible even after manager changes.
 *
 * @param role - The calculated role to check
 * @returns true if the role is a manager role
 */
export const isManagerRole = (role: string | null | undefined): boolean => {
	if (!role) return false
	return ALL_MANAGER_ROLES.includes(role as CalculatedRole)
}

// Roster of calculated roles whose tasks are produced by the GROUP_EVALUATION step type.
// PEER is intentionally excluded: peer tasks come from the separate PEER_EVALUATION step type
// (createPeerEvaluationTasks in tasks.service.ts), so adding PEER here would silently widen
// every isGroupEvalRole consumer (excludeRaterResponses filter in viewers.service.ts, the
// groupEvalSubmissions filter in cycleExecution.service.ts, and the acknowledge groupEvalFilter
// in stepTypeHandlers.ts) into peer-evaluation territory that is unrelated to group eval.
export const GROUP_EVAL_ROLES: CalculatedRole[] = [
	CalculatedRole.RATER,
	CalculatedRole.UP,
	CalculatedRole.DOWN,
	CalculatedRole.DIRECT_REPORTS,
]

export const isGroupEvalRole = (role: string | null | undefined): boolean => {
	if (!role) return false
	return GROUP_EVAL_ROLES.includes(role as CalculatedRole)
}

/**
 * Get the display role for grouping purposes.
 * Co-manager tasks are grouped under PRIMARY_MANAGER for dashboard display.
 */
export const getGroupedRole = (role: CalculatedRole | null): CalculatedRole | null => {
	if (role === CalculatedRole.CO_MANAGER) {
		return CalculatedRole.PRIMARY_MANAGER
	}
	return role
}
